import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import {
  catchError,
  delay,
  endWith,
  filter,
  from,
  lastValueFrom,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs'

import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { ActionDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { AiAnalystAPI } from '@flowx/designer/modules/new-ai/agents/analyst/api/analyst.api'
import { AiAssistantAPI } from '@flowx/designer/modules/new-ai/agents/assistant/api/assistant.api'
import { AiAssistantActions } from '@flowx/designer/modules/new-ai/agents/assistant/store/assistant.actions'
import { AiDesignerAPI } from '@flowx/designer/modules/new-ai/agents/designer/api/designer.api'
import * as NodeActionActions from '@flowx/designer/shared/store/actions/node-action.actions'
import * as NodeValuesActions from '@flowx/designer/shared/store/actions/node-values.actions'
import { ProcessDataModelActions } from '@flowx/designer/shared/store/actions/process-data-model.actions'
import { selectDataModelEntitiesLeaves } from '@flowx/designer/shared/store/selectors/data-model.selectors'

import { AiCoordinatorActions } from './coordinator.actions'

import { AiAgent, InitialMessageRequest } from '../../../common/ai.dictionary'
import { ProjectActionsService } from '../../../store/actions.utils'
// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'
import { AiContextService } from '../../../store/context.service'
import { AiAnalystUtils } from '../../analyst/services/analyst.service'
import { AiAnalystActions } from '../../analyst/store/analyst.actions'
import { AiArchitectAPI } from '../../architect/api/architect.api'
import { AiArchitectActions } from '../../architect/store/architect.actions'
import { AiDesignerActions } from '../../designer/store/designer.actions'
import { AiDeveloperAPI } from '../../developer/api/developer.api'
import { AiCoordinatorApi } from '../api/coordinator.api'

@Injectable()
export class AiCoordinatorEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiAssistantAPI = inject(AiAssistantAPI)
  #aiDesignerAPI = inject(AiDesignerAPI)
  #aiAnalystAPI = inject(AiAnalystAPI)
  #aiDeveloperAPI = inject(AiDeveloperAPI)
  #aiCoordinatorAPI = inject(AiCoordinatorApi)
  #aiArchitectAPI = inject(AiArchitectAPI)
  #projectActionsService = inject(ProjectActionsService)
  #aiContextService = inject(AiContextService)

  getIntro = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getIntro),
      withLatestFrom(
        this.#store.select(AiSelectors.currentUI),
        this.#store.select(AiSelectors.selectedDesignerNodeId),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.pageAgent),
        this.#store.select(AiSelectors.pageLocation),
        this.#store.select(AiSelectors.getScreenContext)
      ),

      map(([, currentUI, selectedDesignerNodeId, , pageAgent, pageLocation, screenContext]) => {
        const isUiFlowPage = pageLocation?.includes('UI_FLOWS')

        switch (pageAgent) {
          case AiAgent.DESIGNER:
            // When on a UI flow page, use a ui_flow-specific action_type
            // so the coordinator/LLM knows we're in a UI flow context.
            if (isUiFlowPage) {
              return {
                agent: pageAgent,
                payload: {
                  continue_editing: false,
                  action_type: 'ui_flow',
                  app_location: pageLocation,
                  screen_context: screenContext,
                  context: null,
                } satisfies InitialMessageRequest,
              }
            }
            return {
              agent: pageAgent,
              payload: {
                continue_editing: false,
                action_type:
                  currentUI && currentUI.templateConfig.length > 0
                    ? selectedDesignerNodeId
                      ? 'user_interface_section'
                      : 'user_interface_modification'
                    : 'user_interface',
                context: currentUI && currentUI.templateConfig.length > 0 ? currentUI : null,
              } satisfies InitialMessageRequest,
            }
          case AiAgent.ASSISTANT:
            return {
              agent: pageAgent,
              payload: {
                continue_editing: false,
                action_type:
                  currentUI && currentUI.templateConfig.length > 0
                    ? selectedDesignerNodeId
                      ? 'user_interface_section'
                      : 'user_interface_modification'
                    : 'user_interface',
                context: currentUI && currentUI.templateConfig.length > 0 ? currentUI : null,
              } satisfies InitialMessageRequest,
            }
          case AiAgent.ANALYST:
            // eslint-disable-next-line no-case-declarations
            const currentProcess = AiAnalystUtils.getInitialProcessContext()
            return {
              agent: pageAgent,
              payload: {
                continue_editing: false,
                action_type:
                  currentProcess &&
                  currentProcess.sl?.length > 0 &&
                  currentProcess.sl.some((sl) => sl.nl.length > 0)
                    ? 'business_process_modification'
                    : 'business_process',
                context: currentProcess || {},
              } satisfies InitialMessageRequest,
            }
          case AiAgent.DEVELOPER: {
            const isReusableFunctionPage = pageLocation?.includes('REUSABLE_FUNCTIONS')
            const isCustomComponentPage = pageLocation?.includes('CUSTOM_COMPONENTS')
            return {
              agent: pageAgent,
              payload: {
                continue_editing: false,
                action_type: isCustomComponentPage
                  ? 'custom_component'
                  : isReusableFunctionPage
                    ? 'reusable_function'
                    : 'business_rule',
                app_location: pageLocation,
                screen_context: screenContext,
                context: null,
              } satisfies InitialMessageRequest,
            }
          }
          case AiAgent.ARCHITECT:
            return {
              agent: pageAgent,
              payload: {
                continue_editing: false,
                action_type: 'data_model',
                context: null,
                app_location: pageLocation,
                screen_context: screenContext,
              } satisfies InitialMessageRequest,
            }
          default:
            return {
              agent: null,
              payload: {
                continue_editing: false,
                action_type: 'initial_message',
                app_location: pageLocation,
                screen_context: screenContext,
                context: null,
              } satisfies InitialMessageRequest,
            }
        }
      }),
      switchMap((initialMessage) => {
        switch (initialMessage?.agent) {
          case AiAgent.DESIGNER:
            return this.#aiDesignerAPI.getSSEActions(initialMessage?.payload).pipe(
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
              map(([response, dataModel]) =>
                AiDesignerActions.getAnswerEventStream({ response, dataModel })
              ),
              catchError(({ status }) =>
                of(
                  AiActions.getAnswerFailure({
                    statusCode: status,
                    errorMsg: null,
                  })
                )
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )

          case AiAgent.ASSISTANT:
            // return this.#aiAssistantAPI.getInitial(initialMessage?.payload).pipe(
            //   takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            //   map((response) => AiAssistantActions.getAnswerEventStream({ response })),
            //   catchError(({ status }) =>
            //     of(
            //       AiActions.getAnswerFailure({
            //         statusCode: status,
            //         errorMsg: null,
            //       })
            //     )
            //   ),
            //   endWith(AiActions.getAnswerStreamCompleted())
            // )
            break
          case AiAgent.ANALYST:
            return this.#aiAnalystAPI.getSSEActions(initialMessage?.payload).pipe(
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              map((response) => AiAnalystActions.getAnswerEventStream({ response })),
              catchError(({ status }) =>
                of(
                  AiActions.getAnswerFailure({
                    statusCode: status,
                    errorMsg: null,
                  })
                )
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )
            break
          case AiAgent.DEVELOPER:
            return this.#aiDeveloperAPI.getSSEActions(initialMessage?.payload).pipe(
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              map((response) => AiAnalystActions.getAnswerEventStream({ response })),
              catchError(({ status }) =>
                of(
                  AiActions.getAnswerFailure({
                    statusCode: status,
                    errorMsg: null,
                  })
                )
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )
            break
          case AiAgent.ARCHITECT:
            return this.#aiArchitectAPI.getSSEActions(initialMessage?.payload).pipe(
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              map((response) => AiArchitectActions.getAnswerEventStream({ response })),
              catchError(({ status }) =>
                of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )
            break
          default:
            return this.#aiCoordinatorAPI.getSSEActions(initialMessage?.payload).pipe(
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
              catchError(({ status }) =>
                of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )
        }
      })
    )
  )

  continueWithPlanAfterGeneratedActions = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.clearGeneratedActions),
      withLatestFrom(
        this.#store.select(AiSelectors.pageLocation),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.generatedIntent),
        this.#store.select(AiSelectors.autonomyLevel),
        this.#store.select(AiSelectors.getAppId),
        this.#store.select(AiSelectors.getAppVersionId)
      ),
      filter(
        ([, pageLocation]) =>
          !!pageLocation && pageLocation === 'PROJECTS->PROCESSES->EXISTING_PROCESS'
      ),
      map(([, pageLocation, session_id, generatedIntent, autonomyLevel, appId, appVersionId]) =>
        AiCoordinatorActions.getIntent({
          prompt:
            'Continue my initial request after executing actions: ' + generatedIntent?.user_message,
          location: pageLocation,
          appId,
          appVersion: appVersionId,
          session_id,
          generatedIntent: null,
          autonomy_level: autonomyLevel === 0 ? 'low' : autonomyLevel === 1 ? 'medium' : 'high',
        })
      )
    )
  )

  continueWithProcessAfterGeneratedActions = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.clearGeneratedActions),
      withLatestFrom(
        this.#store.select(AiSelectors.pageLocation),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.generatedIntent),
        this.#store.select(AiSelectors.autonomyLevel),
        this.#store.select(AiSelectors.getAppId),
        this.#store.select(AiSelectors.getAppVersionId)
      ),
      filter(([, pageLocation]) => !!pageLocation && pageLocation === 'PROJECTS->PROCESSES'),
      map(([, pageLocation, session_id, generatedIntent, autonomyLevel, appId, appVersionId]) =>
        AiCoordinatorActions.getIntent({
          prompt:
            'Continue my initial request after executing actions: ' + generatedIntent?.user_message,
          location: pageLocation,
          appId,
          appVersion: appVersionId,
          session_id,
          generatedIntent: null,
          autonomy_level: autonomyLevel === 0 ? 'low' : autonomyLevel === 1 ? 'medium' : 'high',
        })
      )
    )
  )

  getIntent = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getIntent),
      withLatestFrom(this.#store.select(AiSelectors.getScreenContext)),
      switchMap(
        ([
          { prompt, location, session_id, generatedIntent, autonomy_level, recommended_intent },
          screenContext,
        ]) => {
          try {
            this.#aiContextService.buildCombinedContextSync()
          } catch (error) {
            // silently continue with null context
          }
          return this.#aiCoordinatorAPI
            .getSSEIntent({
              user_message: prompt,
              app_location: location,
              screen_context: screenContext,
              session_id,
              previous_response:
                generatedIntent && generatedIntent.action_required ? generatedIntent : null,
              autonomy_level,
              recommended_intent,
            })
            .pipe(
              map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
              catchError(({ response, status }) =>
                of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: response }))
              ),
              endWith(AiCoordinatorActions.getIntentSuccess())
            )
        }
      )
    )
  )

  routeAfterIntent = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getIntentSuccess),
      withLatestFrom(this.#store.select(AiSelectors.routeToAgent)),
      filter(([, hasPendingRoute]) => hasPendingRoute),
      map(() => AiCoordinatorActions.routeToAgent())
    )
  )

  generatePlan = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.generatePlan),
      switchMap(
        ({ prompt, current_plan, context, session_id, human_feedback, plan_type, document_id }) =>
          this.#aiCoordinatorAPI
            .getSSEPlan({
              user_message: prompt,
              current_plan,
              context,
              session_id,
              human_feedback,
              plan_type,
              document_id: document_id ?? undefined,
            })
            .pipe(
              map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
              catchError(({ status }) =>
                of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )
      )
    )
  )

  onSendMessageAction = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.invokeStep),
      filter(({ selectedStep }) => selectedStep?.step_action === 'send_message'),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      switchMap(([{ selectedStep }, selectedPlan]) => {
        const integratorStepOutput = selectedPlan.actions
          .find((action) => action.agent === 'integrator')
          ?.steps?.find((step) => step.parentAgent === 'integrator')?.executionOutput
        const agentCode = integratorStepOutput?.code
        const inputData =
          integratorStepOutput?.inputs && integratorStepOutput?.inputs.length > 0
            ? '"' + integratorStepOutput?.inputs[0].code + '": "${uploadedFile.minioPath}"'
            : ''

        const actionParams = {
          nodeDefinitionId: selectedStep.step_destination.id,
          name: 'send_message_to_agent',
          order: 1,
          handlerService: 'KafkaSendHandler',
          actionParams: [
            {
              key: 'topicName',
              value: 'ai.flowx.in.agent.builder.v1',
              replaceValues: true,
            },
            {
              key: 'jsonDTOMessage',
              value:
                '{\n  "agent_code": "' +
                agentCode +
                '",\n  "payload": {\n   ' +
                inputData +
                '\n  },\n  "streaming": "false"\n}',
              replaceValues: true,
            },
            {
              key: 'headers',
              value: '{"processInstanceId": ${processInstanceId}}',
              replaceValues: true,
            },
          ],
          timerExpression: '',
          mandatory: false,
          autoRunChildren: false,
          automatic: false,
          manual: false,
          repeatable: false,
          optional: false,
          allowTokenReset: false,
          restartFromSnapshot: false,
          keysForRestart: [],
          keys: [],
          callbackActions: null,
          actionRuleDefinitions: [],
          enableDataMapping: true,
        }

        return this.#projectActionsService.create_send_message_action_to_agent(actionParams).pipe(
          map((response) => AiActions.createSendMessageActionSuccess({ action: response })) // replace with a real success action if available
        )
      })
    )
  )

  onUploadDocumentAction = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.invokeStep),
      filter(({ selectedStep }) => selectedStep?.step_action === 'upload_file'),
      switchMap(({ selectedStep }) => {
        const parentActionParams = {
          nodeDefinitionId: selectedStep.step_destination.id,
          name: 'uploadDocument',
          order: 1,
          handlerService: 'UploadFileHandler',
          actionParams: [
            {
              key: 'topicName',
              value: 'ai.flowx.plugin.document.trigger.persist.document.v1',
              replaceValues: false,
            },
            {
              key: 'documentType',
              value: 'BULK',
              replaceValues: false,
            },
            {
              key: 'customId',
              value: '1234_${processInstanceId}',
              replaceValues: true,
            },
            {
              key: 'headers',
              value:
                '{\n    "processInstanceId": ${processInstanceId},\n    "destinationId": "' +
                selectedStep.step_destination.name +
                '",\n    "callbacksForAction": "uploadDocument"\n}',
              replaceValues: true,
            },
          ],
          actionRuleDefinitions: [],
          callbackActions: [],
          timerExpression: '',
          autoRunChildren: true,
          automatic: false,
          manual: true,
          repeatable: false,
          optional: true,
          allowTokenReset: false,
          restartFromSnapshot: false,
          keysForRestart: [],
          keys: [],
          enableDataMapping: true,
        }

        const childActionParams = {
          name: 'uploadDocument_001',
          handlerService: 'NoOpHandler',
          nodeDefinitionId: selectedStep.step_destination.id,
          actionParams: [],
          actionRuleDefinitions: [
            {
              expression:
                'downloadPath = input.uploadedFile.downloadPath;\nadminLink = "https://admin.theta.flowxai.dev/document/";\n\ndocument = {\n    "name": input.document.name,\n    "minioPath": input.uploadedFile.minioPath,\n    "downloadUrl": "",\n    "status": "Under review"\n};\n\nif(downloadPath != null && downloadPath != ""){\n\n    document.downloadUrl = adminLink + downloadPath;\n\n    output.put("document", document);\n};',
              language: 'JAVA_SCRIPT',
              inputCollection: null,
              outputCollection: null,
              order: 0,
              usesFlattenParams: false,
              businessRuleReferences: [],
              misconfigurations: null,
            },
          ],
          callbackActions: null,
          timerExpression: '',
          order: 1,
          manual: false,
          repeatable: false,
          optional: true,
          autoRunChildren: false,
          allowTokenReset: false,
          restartFromSnapshot: false,
          keysForRestart: [],
          enableDataMapping: true,
        }

        return from(
          (async () => {
            const nodeActions = await lastValueFrom(
              this.#projectActionsService.get_node_actions(selectedStep.step_destination.id)
            )

            const saveDataAction = nodeActions.resourceContent.find(
              (action) => action.name === 'saveData'
            )

            const uploadDocumentAction = nodeActions.resourceContent.find(
              (action) => action.name === 'uploadDocument'
            )
            let parent = null
            if (!uploadDocumentAction) {
              // create parent action for upload document
              parent = await lastValueFrom(
                this.#projectActionsService.create_business_rule_upload_action(parentActionParams)
              )
              const update_parent = await lastValueFrom(
                this.#projectActionsService.update_business_rule_upload_action({
                  ...(parent.resourceContent as ActionDefinition),
                  id: parent.resourceContent.id,
                  name: 'uploadDocument',
                })
              )
              const parentUuid = update_parent.flowxUuid
              await lastValueFrom(
                this.#projectActionsService.create_business_rule_upload_action({
                  ...childActionParams,
                  parentFlowxUuid: parentUuid,
                })
              )

              const valuesUploadDocument = [
                {
                  key: 'topicName',
                  value: 'ai.flowx.engine.receive.plugin.document.persist.document.results.v1',
                },
                {
                  key: 'keyName_ai.flowx.engine.receive.plugin.document.persist.document.results.v1',
                  value: 'uploadedFile',
                },
              ]

              await lastValueFrom(
                this.#projectActionsService.create_receive_message_action_to_agent(
                  selectedStep.step_destination.id,
                  valuesUploadDocument
                )
              )
            }
            // add template config action
            const templateConfig = await lastValueFrom(
              this.#projectActionsService.get_template_config_id(
                selectedStep.step_destination.areaId
              )
            )

            // find FILE_UPLOAD components and extract their ids
            const fileUploadIds: number[] = []
            for (const navAreaKey in templateConfig) {
              if (!Object.prototype.hasOwnProperty.call(templateConfig, navAreaKey)) {
                continue
              }
              const navArea = (templateConfig as any)[navAreaKey]
              if (!navArea || typeof navArea !== 'object') {
                continue
              }
              for (const cfgKey in navArea) {
                if (!Object.prototype.hasOwnProperty.call(navArea, cfgKey)) {
                  continue
                }
                const cfg = navArea[cfgKey]
                if (cfg && cfg.componentIdentifier === 'FILE_UPLOAD') {
                  fileUploadIds.push(cfg.id)
                }
              }
            }
            const fileUploadId = fileUploadIds.length > 0 ? fileUploadIds[0] : null

            const uiActionParams = {
              templateConfigId: fileUploadId,
              type: 'UPLOAD',
              name: 'uploadDocument',
              actionFlowxUuid: uploadDocumentAction
                ? uploadDocumentAction.flowxUuid
                : parent.resourceContent?.flowxUuid,
              customBody: '',
              triggerEvent: 'CLICK',
              params: {
                blocksUi: '',
                dismissProcess: '',
                formsToSubmit: [],
                customKeys: ['document.name'],
                excludedKeys: [''],
              },
            }

            await lastValueFrom(
              this.#projectActionsService.create_ui_template_action(uiActionParams)
            )

            // find all BUTTON components and extract their ids
            const buttonIds: number[] = []
            for (const navAreaKey in templateConfig) {
              if (!Object.prototype.hasOwnProperty.call(templateConfig, navAreaKey)) {
                continue
              }
              const navArea = (templateConfig as any)[navAreaKey]
              if (!navArea || typeof navArea !== 'object') {
                continue
              }
              for (const cfgKey in navArea) {
                if (!Object.prototype.hasOwnProperty.call(navArea, cfgKey)) {
                  continue
                }
                const cfg = navArea[cfgKey]
                if (cfg && cfg.componentIdentifier === 'BUTTON') {
                  buttonIds.push(cfg.id)
                }
              }
            }
            for (const buttonId of buttonIds) {
              const saveActionParams = {
                templateConfigId: buttonId,
                type: 'ACTION',
                name: 'saveData',
                actionFlowxUuid: saveDataAction.flowxUuid,
                customBody: '',
                triggerEvent: 'CLICK',
                params: {
                  blocksUi: '',
                  dismissProcess: '',
                  formsToSubmit: [],
                  customKeys: [''],
                  excludedKeys: [''],
                },
              }
              await lastValueFrom(
                this.#projectActionsService.create_ui_template_action(saveActionParams)
              )
            }

            return AiActions.createBusinessRuleUploadActionSuccess({ action: parent })
          })()
        )
      })
    )
  )

  onReceiveMessageAction = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.invokeStep),
      filter(({ selectedStep }) => selectedStep?.step_action === 'receive_message'),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      switchMap(([{ selectedStep }, selectedPlan]) => {
        const integratorStepOutput = selectedPlan.actions
          .find((action) => action.agent === 'integrator')
          ?.steps?.find((step) => step.parentAgent === 'integrator')?.executionOutput

        const agentOutput = integratorStepOutput?.out_topic

        const nodeDefinitionId = selectedStep.step_destination.id
        const values = []
        values.push({
          value: 'agentOutput',
          key: 'keyName_' + agentOutput,
        })
        values.push({
          value: agentOutput,
          key: 'topicName',
        })
        return this.#projectActionsService
          .create_receive_message_action_to_agent(nodeDefinitionId, values)
          .pipe(
            map((response) => AiActions.createReceiveMessageActionSuccess({ action: response })) // replace with a real success action if available
          )
      })
    )
  )

  onCreateSendMessageActionSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.createSendMessageActionSuccess),
      switchMap(({ action }) => {
        const nodeValues = Array.isArray(action.resourceContent)
          ? action.resourceContent
          : (action.resourceContent?.nodeValues ?? [action.resourceContent].filter(Boolean))

        return of(NodeValuesActions.createNodeValuesSuccess({ nodeValues })).pipe(delay(2000))
      })
    )
  )

  onKeepAgentDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.keepAgentDraft),
      map(({ response }) => AiCoordinatorActions.keepAgentDraftSuccess({ response }))
    )
  )

  mapDestination = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.keepProcessDraftSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedPlan),
        this.#store.select(AiSelectors.currentPlanStep)
      ),
      filter(([, , currentPlanStep]) => currentPlanStep !== null),
      switchMap(([, currentPlan]) => {
        const currentProcess = AiAnalystUtils.getInitialProcessContext()
        return this.#aiCoordinatorAPI
          .getSSEMapDestination({
            current_plan: currentPlan,
            context: currentProcess,
            session_id: this.#store.selectSignal(AiSelectors.sessionId)(),
          })
          .pipe(
            map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      })
    )
  )

  automateProcess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.automateProcess),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      switchMap(([, selectedPlan]) => {
        const currentProcess = AiAnalystUtils.getInitialProcessContext()
        return this.#aiCoordinatorAPI
          .getSSEBusinessAgents({
            user_message: 'Automate the process with AI Business Agents',
            process_context: currentProcess,
            current_plan: selectedPlan,
            session_id: this.#store.selectSignal(AiSelectors.sessionId)(),
          })
          .pipe(
            map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      })
    )
  )

  implementAgent = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.implementAgent),
      withLatestFrom(
        this.#store.select(AiSelectors.getAppId),
        this.#store.select(AiSelectors.getAppVersionId)
      ),
      switchMap(([{ agent }, appId, appVersionId]) =>
        this.#projectActionsService
          .parse_actions_to_run(
            'create_agent',
            {
              agent_name: agent.agent,
              agent_code: agent.agent,
              agent_description: agent.description,
            },
            appId,
            appVersionId
          )
          .pipe(map((response) => ({ response, action_name: 'create_agent' })))
      ),
      map(({ response, action_name }) =>
        AiCoordinatorActions.generateActionSuccess({ response_action: response, action_name })
      )
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAssistantActions.sendFeedback),
        switchMap(({ payload }) => this.#aiAssistantAPI.sendFeedback(payload))
      ),
    { dispatch: false }
  )

  generatedAction = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.generateAction),
      withLatestFrom(
        this.#store.select(AiSelectors.getAppId),
        this.#store.select(AiSelectors.getAppVersionId)
      ),
      switchMap(([{ action_name, action_parameters }, appId, appVersionId]) =>
        this.#projectActionsService
          .parse_actions_to_run(action_name, action_parameters, appId, appVersionId)
          .pipe(map((response) => ({ response, action_name })))
      ),
      map(({ response, action_name }) =>
        AiCoordinatorActions.generateActionSuccess({ response_action: response, action_name })
      )
    )
  )

  implementAgentSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.generateActionSuccess),
      filter(({ action_name }) => action_name === 'create_agent'),
      map(({ response_action }) =>
        AiCoordinatorActions.generateAgentWorkflow({ agent: response_action })
      )
    )
  )

  generatePromptForAgent = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.generatePromptForAgent),
      switchMap(({ agent }) =>
        this.#aiCoordinatorAPI
          .getSSEPromptForAgent({
            agent_name: agent.name,
            agent_description: agent.description,
            session_id: this.#store.selectSignal(AiSelectors.sessionId)(),
          })
          .pipe(
            map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      )
    )
  )

  generatePromptForStep = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.generatePromptForStep),
      switchMap(({ step, agent }) =>
        this.#aiCoordinatorAPI
          .getSSEPromptForStep({
            agent_name: agent,
            step_number: step.step_number,
            step_name: step.step_name,
            step_description: step.step_description,
            step_action: step.step_action,
            session_id: this.#store.selectSignal(AiSelectors.sessionId)(),
          })
          .pipe(
            map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      )
    )
  )

  generateAgentWorkflow = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.generateAgentWorkflow),
      switchMap(({ agent }) =>
        this.#aiCoordinatorAPI
          .getSSEAgentWorkflow({
            agent_description: agent.description,
            session_id: this.#store.selectSignal(AiSelectors.sessionId)(),
          })
          .pipe(
            map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      )
    )
  )

  createPlanAfterCloseMessage$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getAnswerEventStream), // or your custom action
      filter(({ response }) => !!(response as any)?.message?.end?.answer?.close_message), // or whatever condition matches
      map(() => AiCoordinatorActions.createPlan())
    )
  )

  updatePlanAfterMapDestinationSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getAnswerEventStream),
      filter(({ response }) => !!(response as any)?.message?.end?.answer?.map_destinations),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      map(([, selectedPlan]) =>
        AiCoordinatorActions.updatePlan({
          plan_id: selectedPlan.id,
          plan: selectedPlan,
          status: 'in progress',
        })
      )
    )
  )

  createSendMessageActionSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.createSendMessageActionSuccess),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      map(([, selectedPlan]) =>
        AiCoordinatorActions.updatePlan({
          plan_id: selectedPlan.id,
          plan: selectedPlan,
          status: 'in progress',
        })
      )
    )
  )

  createBusinessRuleUploadActionSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.createBusinessRuleUploadActionSuccess),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      map(([, selectedPlan]) =>
        AiCoordinatorActions.updatePlan({
          plan_id: selectedPlan.id,
          plan: selectedPlan,
          status: 'in progress',
        })
      )
    )
  )

  createReceiveMessageActionSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.createReceiveMessageActionSuccess),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      map(([, selectedPlan]) =>
        AiCoordinatorActions.updatePlan({
          plan_id: selectedPlan.id,
          plan: selectedPlan,
          status: 'in progress',
        })
      )
    )
  )

  createDataModelActionSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(ProcessDataModelActions.keepTemporaryDataModelSuccess),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      map(([, selectedPlan]) =>
        AiCoordinatorActions.updatePlan({
          plan_id: selectedPlan.id,
          plan: selectedPlan,
          status: 'in progress',
        })
      )
    )
  )

  onNodeActionSuccessRefresh = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        AiActions.createSendMessageActionSuccess,
        AiActions.createReceiveMessageActionSuccess,
        AiActions.createBusinessRuleUploadActionSuccess
      ),
      delay(1000),
      tap(() => BpmnEditorEvents.nodeActionCloseConfigPanel()),
      map(({ action }) => action.resourceContent.nodeDefinitionId),
      switchMap((nodeId) => {
        if (nodeId) {
          return of(NodeActionActions.getNodeActions({ nodeId })).pipe(delay(2000))
        }
        return of(null)
      })
    )
  )

  createPlan = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.createPlan),
      withLatestFrom(
        this.#store.select(AiSelectors.getAppId),
        this.#store.select(AiSelectors.getAppVersionId),
        this.#store.select(AiSelectors.getProcessId),
        this.#store.select(AiSelectors.getProcessName),
        this.#store.select(AiSelectors.selectedPlan)
      ),
      switchMap(([, appId, appVersionId, processId, processName, selectedPlan]) =>
        this.#aiCoordinatorAPI
          .createPlan({
            application_id: appId,
            application_version_id: appVersionId,
            process_id: processId || '',
            process_name: processName || '',
            plan_name: selectedPlan?.name,
            plan_description: selectedPlan?.description,
            plan_icon: selectedPlan?.icon,
            plan: selectedPlan,
            author: 'coordinator',
          })
          .pipe(
            map((payload) => AiCoordinatorActions.createPlanSuccess({ plan: payload })),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            )
          )
      )
    )
  )

  getAllPlans = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getAllPlans),
      withLatestFrom(
        this.#store.select(AiSelectors.getAppId),
        this.#store.select(AiSelectors.getAppVersionId),
        this.#store.select(AiSelectors.getProcessId)
      ),
      switchMap(([, appId, appVersionId, processId]) =>
        this.#aiCoordinatorAPI.getAllPlans({
          application_id: appId,
          application_version_id: appVersionId,
          process_id: processId,
        })
      ),
      map((response) => AiCoordinatorActions.getAllPlansSuccess({ plans: response }))
    )
  )

  getPlan = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getPlan),
      switchMap(({ plan_id }) => this.#aiCoordinatorAPI.getPlan({ plan_id })),
      map((response) => AiCoordinatorActions.getPlanSuccess({ plan: response }))
    )
  )

  getNextStepsPlanSelected = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.getPlanSuccess),
      switchMap(({ plan }) => {
        const currentProcess = AiAnalystUtils.getInitialProcessContext()
        return this.#aiCoordinatorAPI
          .getSSENextStepSelectedPlan({
            current_plan: plan,
            context: currentProcess,
            session_id: this.#store.selectSignal(AiSelectors.sessionId)(),
          })
          .pipe(
            map((response) => AiCoordinatorActions.getAnswerEventStream({ response })),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      })
    )
  )

  updatePlan = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.updatePlan),
      switchMap(({ plan_id, plan, status }) =>
        this.#aiCoordinatorAPI.updatePlan({ plan_id, plan, status, author: 'coordinator' })
      ),
      map((response) => AiCoordinatorActions.updatePlanSuccess({ plan: response }))
    )
  )

  updatePlanSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.updatePlanSuccess),
      withLatestFrom(this.#store.select(AiSelectors.selectedPlan)),
      map(([, selectedPlan]) => {
        const currentProcess = AiAnalystUtils.getInitialProcessContext()
        return AiCoordinatorActions.mapDestination({
          current_plan: selectedPlan,
          context: currentProcess,
          session_id: this.#store.selectSignal(AiSelectors.sessionId)(),
        })
      })
    )
  )

  deletePlan = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.deletePlan),
      switchMap(({ plan_id }) => this.#aiCoordinatorAPI.deletePlan({ plan_id })),
      map(() => AiCoordinatorActions.deletePlanSuccess())
    )
  )

  stepInvoked$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.stepInvoked),
      map(() => AiCoordinatorActions.routeToAgent())
    )
  )
}
